import FilterIcon from "assets/images/FilterIcon";
import blackArrowIcon from "assets/images/black-arrow-icon.svg";
import classNames from "classnames";
import {format, parse} from "date-fns";
import {useState, useEffect} from "react";
import {Link} from "react-router-dom";
import {common as stringsCommon, medications as strings} from "strings";

import AlertModal from "components/AlertModal";
import Button from "components/Button";
import ConfirmRefillRequest from "components/ConfirmRefillRequest";
import DateRangeSelect, {
  DateRangeOption,
  DateRange,
  DATE_RANGE_OPTIONS,
  DEFAULT_DATE_FILTER,
} from "components/DateRangeSelect";
import Divider from "components/Divider";
import LoadingIndicator from "components/LoadingIndicator";
import MedicationsFilterModal, {
  FilterOptions,
  DEFAULT_FILTERS,
} from "components/MedicationsFilterModal";
import PageTitle from "components/PageTitle";
import RadioButton from "components/RadioButton";
import RefillPlacedModal from "components/RefillPlacedModal";
import SearchBar from "components/SearchBar";
import Select from "components/Select";
import TitlePatientNameSelect from "components/TitlePatientNameSelect";

import * as models from "liberty-core/api/models";
import {usePrescriptionsInfinite} from "liberty-core/api/react-query/prescription";
import {useRefillRequestRegistered} from "liberty-core/api/react-query/refillRequest";

import {useAlert} from "lib/alert";
import {useAuthenticatedUser} from "lib/auth";
import useCurrentPatientId from "lib/useCurrentPatientId";
import {useDebounce} from "lib/useDebounce";

import styles from "./MedicationsPage.module.css";

type MedicationProps = {
  refillable: boolean;
  pillPicture: string;
  rxNumber: string;
  statusText: string;
  dispenseDate?: string | null;
  writtenDate: string;
  medicationName: string;
  status: models.PrescriptionStatus;
  refillProgram: models.PrescriptionRefillProgram | null;

  isRefillMultipleToggled: boolean;
  isSelected: boolean;
  onClick: () => void;

  isLastMedication: boolean;
};

function MedicationButton(props: MedicationProps) {
  const displayStatuses = {
    Scheduled: strings.scheduled,
    Processing: strings.processing,
    ReadyForPickup: strings.readyForPickup,
    OutOfRefills: strings.outOfRefills,
  };

  const disabled = !props.refillable && props.isRefillMultipleToggled;

  return (
    <button
      className={classNames(
        styles.medicationButtonContainer,
        props.isLastMedication && styles.lastMedication,
        disabled && styles.disabled,
      )}
      onClick={
        disabled
          ? undefined
          : props.isRefillMultipleToggled
          ? props.onClick
          : () => {
              return;
            }
      }
    >
      <div className={styles.buttonContent}>
        <div className={styles.medicationNameContainer}>
          <div className={classNames(styles.normalText, styles.medicationName)}>
            {props.medicationName}
          </div>
          {props.refillProgram ? (
            <div className={styles.refillProgram}>{props.refillProgram}</div>
          ) : null}
        </div>
        <div className={styles.buttonLowerContent}>
          {props.isRefillMultipleToggled ? (
            <RadioButton
              className={styles.radioButton}
              checked={props.isSelected}
            />
          ) : null}
          <img
            src={props.pillPicture}
            alt=""
            className={styles.medicationImage}
          />
          <div className={styles.mainInfoContainer}>
            <div className={styles.medicationInfo}>
              <div className={styles.infoLeftText}>
                <div className={styles.rxNumberText}>
                  {stringsCommon.rxNum + " " + props.rxNumber}
                </div>
                <div className={styles.buttonText}>{props.statusText}</div>
              </div>
              <div className={styles.dateContainer}>
                <div className={styles.infoRightText}>
                  <div className={styles.buttonText}>
                    {props.dispenseDate
                      ? strings.lastFilledOn
                      : strings.writtenOn}
                  </div>
                  <div className={styles.smallerText}>
                    {props.dispenseDate
                      ? new Date(props.dispenseDate).toLocaleDateString()
                      : new Date(props.writtenDate).toLocaleDateString()}
                  </div>
                </div>
                {!props.isRefillMultipleToggled ? (
                  <img
                    className={styles.blackArrow}
                    src={blackArrowIcon}
                    alt=""
                  />
                ) : null}
              </div>
            </div>
            {props.status !== "DoNotShow" ? (
              <div className={styles.medicationStatus}>
                {props.status !== "OutOfRefills" ? (
                  <div className={styles.statusCircle} />
                ) : null}
                <div
                  className={classNames(
                    styles.smallerText,
                    styles.statusText,
                    props.status === "OutOfRefills" && styles.outOfRefills,
                  )}
                >
                  {displayStatuses[props.status]}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      {!props.isLastMedication ? <Divider /> : null}
    </button>
  );
}

function getStartDate(dateFilter: string) {
  const startDate = new Date();
  switch (dateFilter) {
    case "last-7-days":
      startDate.setDate(startDate.getDate() - 7);
      break;
    case "last-30-days":
      startDate.setDate(startDate.getDate() - 30);
      break;
    case "last-90-days":
      startDate.setDate(startDate.getDate() - 90);
      break;
    case "last-6-months":
      startDate.setMonth(startDate.getMonth() - 6);
      break;
    case "last-12-months":
      startDate.setMonth(startDate.getMonth() - 12);
      break;
    case "last-24-months":
      startDate.setMonth(startDate.getMonth() - 24);
      break;
    default:
      // custom date: format is already in "{startDate}to{endDate}"
      return dateFilter.split("to")[0];
  }

  return format(startDate, "yyyy-MM-dd");
}

function getDateRangeText(dateRange: string) {
  switch (dateRange) {
    case "last-7-days":
      return strings.lastDays(7);
    case "last-30-days":
      return strings.lastDays(30);
    case "last-90-days":
      return strings.lastDays(90);
    case "last-6-months":
      return strings.lastMonths(6);
    case "last-12-months":
      return strings.lastMonths(12);
    case "last-24-months":
      return strings.lastMonths(24);
    default:
      return strings.specifiedDateRange;
  }
}

function getEndDate(dateFilter: string) {
  if (dateFilter.startsWith("last")) {
    return format(new Date(), "yyyy-MM-dd");
  }

  // custom date: format is already in "{startDate}to{endDate}"
  return dateFilter.split("to")[1];
}

const getOriginalDateRange = (
  dateRange: DateRangeOption | DateRange,
): DateRangeOption => {
  const result = DATE_RANGE_OPTIONS.filter((obj) => obj.value === dateRange);
  if (result.length === 0) return "custom";
  else return result[0].value;
};

const PAGE_SIZE = 8;
const sortOptions = [
  {label: strings.lastFilled, value: "lastFilled" as const},
  {label: strings.drugName, value: "drugName" as const},
];

export default function MedicationsPage() {
  const [authenticatedUser] = useAuthenticatedUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, pending] = useDebounce(searchQuery, 500);

  const [dateRangeFilter, setDateRangeFilter] = useState(DEFAULT_DATE_FILTER);
  const [dateRangeSelect, setDateRangeSelect] = useState(
    getOriginalDateRange(DEFAULT_DATE_FILTER),
  );

  const [startDate, setStartDate] = useState(getStartDate(DEFAULT_DATE_FILTER));
  const [endDate, setEndDate] = useState(getEndDate(DEFAULT_DATE_FILTER));

  const [sortingSelection, setSortingSelection] =
    useState<typeof sortOptions[number]["value"]>("lastFilled");
  const [isRefillMultipleToggled, setIsRefillMultipleToggled] = useState(false);
  const [selectedMedications, setSelectedMedications] = useState<number[]>([]);

  const [showFilterModal, setShowFilterModal] = useState(false);

  const [showConfirmRefillModal, setShowConfirmRefillModal] = useState(false);
  const [showRefillPlacedModal, setShowRefillPlacedModal] = useState(false);
  const [pickUpDate, setPickUpDate] = useState<Date | undefined>();
  const [pickUpTime, setPickUpTime] = useState<models.TimeString>();
  const [comments, setComments] = useState("");

  const alert = useAlert();
  const {selectedPatientId, updateSelectedPatientId} = useCurrentPatientId(
    authenticatedUser?.data.linkedPatients ?? [],
  );

  const {
    mutateAsync: confirmRefillRequestMutation,
    isLoading: isRefillRequestLoading,
  } = useRefillRequestRegistered();

  const [filters, setFilters] = useState<FilterOptions>(DEFAULT_FILTERS);

  const [showAlertModal, setShowAlertModal] = useState(false);
  const [refillResponse, setRefillResponse] = useState<models.RefillResponse[]>(
    [],
  );

  const isInvalidDateRange =
    startDate === "" || endDate === "" || startDate.localeCompare(endDate) > 0;
  const {
    data: medicationsPages,
    isLoading,
    status,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePrescriptionsInfinite({
    startDate: isInvalidDateRange ? DEFAULT_DATE_FILTER : startDate,
    endDate: isInvalidDateRange ? DEFAULT_DATE_FILTER : endDate,
    pageSize: PAGE_SIZE,
    search: debouncedSearchQuery.toLowerCase(),
    sortBy: sortingSelection,
    readyForRefill: filters.readyForRefill,
    onFile: filters.onFile,
    inProgress: filters.inProgress,
    requiresRenewal: filters.requiresRenewal,
    scheduled: filters.scheduled,
    readyForPickup: filters.readyForPickup,
    patientId: selectedPatientId,
  });

  const selectedMedicationsForModal = medicationsPages?.pages
    .map((p) => p.prescriptions)
    .flat()
    .filter((medication) => {
      return selectedMedications.includes(medication.scriptNumber);
    })
    .map((medication) => {
      return {
        scriptNumber: medication.scriptNumber,
        name: medication.drugPrescribed.name,
        quantity: medication.quantity,
        imageUrl: medication.drugPrescribed.imageUrl,
      };
    });

  function renderMedication(
    curMedication: models.Prescription,
    currentIndex: number,
  ) {
    return (
      <MedicationButton
        key={curMedication.scriptNumber}
        refillable={curMedication.refillable}
        pillPicture={curMedication.drugPrescribed.imageUrl}
        rxNumber={curMedication.scriptNumber.toString()}
        statusText={curMedication.statusText}
        dispenseDate={curMedication.fills[0].dispenseDate}
        writtenDate={curMedication.writtenDate}
        medicationName={curMedication.drugPrescribed.name}
        status={curMedication.status}
        isRefillMultipleToggled={isRefillMultipleToggled}
        isSelected={selectedMedications.includes(curMedication.scriptNumber)}
        refillProgram={curMedication.refillProgram}
        onClick={() => {
          setSelectedMedications((prevList) => {
            let newList = [...prevList];
            if (prevList.includes(curMedication.scriptNumber)) {
              newList = newList.filter(
                (scriptNumber) => scriptNumber !== curMedication.scriptNumber,
              );
            } else {
              newList.push(curMedication.scriptNumber);
            }

            return newList;
          });
        }}
        isLastMedication={
          !!medicationsPages &&
          currentIndex === medicationsPages.pages.length * PAGE_SIZE
        }
      />
    );
  }

  useEffect(() => {
    if (status === "error") {
      setShowAlertModal(true);
    }
  }, [status]);

  return (
    <div className={styles.container}>
      <MedicationsFilterModal
        visible={showFilterModal}
        filterOptions={filters}
        onClickConfirm={(newFilters) => {
          setFilters(newFilters);
        }}
        onClickCancel={() => setShowFilterModal(false)}
      />
      <div className={styles.stickyMedicationsHeader}>
        <div className={styles.titleScreen}>
          {authenticatedUser?.data.linkedPatients?.length ? (
            <TitlePatientNameSelect
              defaultPatientName={authenticatedUser.data.name}
              linkedPatients={authenticatedUser.data.linkedPatients}
              selectedPatientId={selectedPatientId}
              onChange={updateSelectedPatientId}
            />
          ) : (
            <PageTitle className={styles.titleText}>{strings.title}</PageTitle>
          )}
          <button
            className={styles.filterMobileButton}
            onClick={() => setShowFilterModal(true)}
          >
            <FilterIcon />
          </button>
        </div>
        <div className={styles.searchFunctionality}>
          <SearchBar
            className={styles.searchBar}
            prompt={strings.search}
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <Button
            className={styles.filterDesktopButton}
            variant="unfilled"
            iconRight={(color: string) => <FilterIcon color={color} />}
            onClick={() => setShowFilterModal(true)}
          >
            {strings.filter}
          </Button>
        </div>
        <div className={styles.dateRangeContainer}>
          <span className={styles.bodyText}>{strings.showMeds}</span>
          <DateRangeSelect
            dateRangeSelect={dateRangeSelect}
            setDateRangeSelect={setDateRangeSelect}
            setDateFilter={(value: DateRangeOption | DateRange) => {
              setDateRangeFilter(value);
              setStartDate(getStartDate(value));
              setEndDate(getEndDate(value));
            }}
          />
        </div>
        <div className={styles.selectionContainer}>
          <span className={styles.sortingSelect}>
            <span className={styles.sortByText}>{strings.sortBy}</span>
            <Select
              className={styles.selectComponent}
              selectClassName={styles.selectBorder}
              optionClassName={styles.selectOption}
              options={sortOptions}
              value={sortingSelection}
              onChange={setSortingSelection}
              placeholder=""
            />
          </span>
          <Button
            className={styles.refillButton}
            labelClassName={styles.refillText}
            variant="minimal"
            onClick={() => {
              setIsRefillMultipleToggled((prevValue) => !prevValue);
              setSelectedMedications([]);
            }}
          >
            {isRefillMultipleToggled
              ? stringsCommon.cancel
              : strings.refillMultiple}
          </Button>
        </div>
        <Divider className={styles.mainLineDivider} />
      </div>
      {pending || isRefillRequestLoading || isLoading ? (
        <LoadingIndicator className={styles.loadingIndicator} />
      ) : isInvalidDateRange ? (
        <div className={styles.noMedicationsText}>
          {strings.invalidDateRange}
        </div>
      ) : (
        <div className={styles.medicationListContainer}>
          {medicationsPages &&
          medicationsPages.pages.length > 0 &&
          medicationsPages.pages[0].totalPrescriptions > 0 ? (
            <>
              {medicationsPages?.pages
                .flatMap((p) => p.prescriptions)
                .map((medication, i) => {
                  return isRefillMultipleToggled ? (
                    renderMedication(medication, i)
                  ) : (
                    <Link
                      className={styles.medicationLink}
                      to={`/medications/${medication.scriptNumber}`}
                      key={`${medication.scriptNumber}${i}`}
                    >
                      {renderMedication(medication, i)}
                    </Link>
                  );
                })}
              {hasNextPage ? (
                <div className={styles.loadMoreButtonContainer}>
                  <Button
                    className={styles.loadMoreButton}
                    loading={isFetchingNextPage}
                    onClick={() => fetchNextPage()}
                  >
                    {strings.loadMore}
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
            <div className={styles.noMedicationsText}>
              {`${strings.noMedsIn} ${getDateRangeText(dateRangeFilter)}`}
            </div>
          )}
        </div>
      )}
      {isRefillMultipleToggled ? (
        <div className={styles.stickyButtonContainer}>
          <Button
            className={classNames(
              styles.requestRefillButton,
              !isRefillMultipleToggled && styles.invisibleButton,
            )}
            disabled={selectedMedications.length === 0}
            onClick={() => {
              setShowConfirmRefillModal(true);
            }}
          >
            {strings.requestRefill}
          </Button>
        </div>
      ) : null}
      <ConfirmRefillRequest
        visible={showConfirmRefillModal}
        onPickDate={(dateChosen) => {
          setPickUpDate(dateChosen);
        }}
        onPickTime={(timeChosen: models.TimeString) => {
          setPickUpTime(timeChosen);
        }}
        onClickCancel={() => {
          setShowConfirmRefillModal(false);
        }}
        onClickRequest={() => {
          if (pickUpDate && pickUpTime) {
            const pickUpDateTime = parse(pickUpTime, "HH:mm", pickUpDate);
            const refillRequestData = {
              scriptNumbers: selectedMedications,
              pickupDateTime: pickUpDateTime.toISOString(),
              comment: "",
            };
            confirmRefillRequestMutation(refillRequestData)
              .then((res) => {
                setShowRefillPlacedModal(true);
                setRefillResponse(res);
              })
              .catch(() => {
                alert({
                  title: stringsCommon.whoops,
                  message: strings.problemProcessing,
                });
              })
              .finally(() => {
                setShowConfirmRefillModal(false);
                setIsRefillMultipleToggled(false);
                setPickUpDate(undefined);
                setPickUpTime("");
              });
          }
          return;
        }}
        med={selectedMedicationsForModal ?? []}
        datePicked={pickUpDate}
        timePicked={pickUpTime}
        loading={isRefillRequestLoading}
        comment={comments}
        onComment={setComments}
      />
      <RefillPlacedModal
        medicationList={selectedMedicationsForModal ?? []}
        refillResponse={refillResponse}
        visible={showRefillPlacedModal}
        onClickClose={() => {
          setShowRefillPlacedModal(false);
          setSelectedMedications([]);
          setRefillResponse([]);
        }}
      />
      <AlertModal
        title={stringsCommon.whoops}
        message={strings.problemLoading}
        visible={showAlertModal}
        onClickConfirm={() => setShowAlertModal(false)}
      />
    </div>
  );
}
